import React, { useCallback, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';
import { Table } from '@common-ui-library/molecule';
import { logger, useI18n, useBIEvents } from '@common-ui-library/shared';
import { Alert, Button, Box } from '@common-ui-library/atom';

import { BASE_URL } from '../../Utils/utils';
import { userManagementColumnProps, entityConfig } from '../Props';
import UserManagementEntityHeader from './UMEntityHeader';
import UserManagementEntityDataEntryDialog from './UMEntityDataEntryDialog';
import { useUserManagementStore } from '../Store/useUserManagementStore';
import { useUserManagementData } from '../Hooks/useUserManagementData';
import { useShallow } from 'zustand/react/shallow';
import { NotFoundPage } from '../../EleosAdminPortal/NotFoundPage';
import {
  getPaginateEvent,
  getPageSizeChangeEvent,
  getLoadErrorEvent,
  getRetryFetchEvent,
} from '../../Utils/biEvents';

const UserManagementEntityTable = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { sendBIEvent } = useBIEvents();
  const { 
    isValidEntityStatus, 
    service 
  } = useUserManagementData();
  
  // Track previous page size for page size change detection
  const prevPageSizeRef = useRef<number | null>(null);
  
  const {
    isLoading,
    items,
    selectedItem,
    userManagementEntity,
    pagination,
    setState,
    fetchEntities,
    fetchError,
    setFetchError,
  } = useUserManagementStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      items: state.items,
      selectedItem: state.selectedItem,
      userManagementEntity: state.userManagementEntity,
      pagination: state.pagination,
      setState: state.setState,
      fetchEntities: state.fetchEntities,
      fetchError: state.fetchError,
      setFetchError: state.setFetchError,
    }))
  );

  // Reset page size ref when entity changes (switching between providers/sites/users)
  useEffect(() => {
    prevPageSizeRef.current = null;
  }, [userManagementEntity]);

  const handleRowClick = useCallback((item: any) => {
    setState({ selectedItem: item });
    navigate(`${BASE_URL}${userManagementEntity}/${item.id}`);
  }, [userManagementEntity, setState, navigate]);

  const onPageChange = useCallback(async (page: number, rowsPerPage: number) => {
      logger.info('onPageChange', page, rowsPerPage);
      
      // Detect page size change using ref (more reliable than comparing with pagination state)
      const isPageSizeChange = prevPageSizeRef.current !== null && prevPageSizeRef.current !== rowsPerPage;
      
      // Emit BI event: page size change vs paginate
      if (userManagementEntity) {
        if (isPageSizeChange) {
          sendBIEvent(getPageSizeChangeEvent(userManagementEntity), {
            field_name: "page_size",
            field_content: rowsPerPage,
            field_prev_value: prevPageSizeRef.current
          });
        } else {
          sendBIEvent(getPaginateEvent(userManagementEntity), {
            field_name: "page_number",
            field_content: page + 1,
          });
        }
      }
      prevPageSizeRef.current = rowsPerPage;
      
      // Reset to page 1 if page size changed (items per page changed, current page may not exist)
      // Otherwise use the requested page (Table is 0-indexed, API is 1-indexed)
      const targetPage = isPageSizeChange ? 1 : page + 1;
      
      if (userManagementEntity) {
          await fetchEntities(service, userManagementEntity, { page: targetPage, rowsPerPage });
      }

  }, [service, userManagementEntity, fetchEntities, sendBIEvent]);

  const handleRetry = useCallback(() => {
    logger.info('Retrying fetch after error');
    
    // Emit retry fetch BI event
    if (userManagementEntity) {
      sendBIEvent(getRetryFetchEvent(userManagementEntity), {
        path: window.location.pathname,
      });
    }
    
    setFetchError({ status: false, message: '' });
    if (userManagementEntity) {
      fetchEntities(service, userManagementEntity, { page: 1 });
    }
  }, [service, userManagementEntity, fetchEntities, setFetchError, sendBIEvent]);

  // Emit load error BI event when fetchError occurs
  useEffect(() => {
    if (fetchError.status && userManagementEntity) {
      sendBIEvent(getLoadErrorEvent(userManagementEntity), {
        message: fetchError.message,
      });
    }
  }, [fetchError.status, fetchError.message, userManagementEntity, sendBIEvent]);

  if (!isValidEntityStatus) return <NotFoundPage />;
  if (!userManagementEntity) return <React.Fragment />;

  return (
    <>
      <UserManagementEntityHeader />
      {fetchError.status && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                {t('actions.retry')}
              </Button>
            }
          >
            {fetchError.message || t('errors.fetchFailed')}
          </Alert>
        </Box>
      )}
      <Table
        columns={userManagementColumnProps[userManagementEntity]}
        data={items}
        loading={
          !items ||
          (items.length === 0 && pagination?.total_count !== 0 && !fetchError.status) ||
          isLoading
        }
        page={pagination?.current_page ? pagination.current_page - 1 : 0}
        totalCount={pagination?.total_count}
        onPageChange={onPageChange}
        onRowClick={entityConfig[userManagementEntity]?.isRowClickable ? handleRowClick : undefined}
        selectedId={selectedItem?.id}
      />
      <UserManagementEntityDataEntryDialog />
    </>
  );
};

export default UserManagementEntityTable;
